import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  Avatar,
  Button,
  CopyableField,
  DateTimeTz,
  LoadingIcon,
} from "@web-app/components";
import { UberResponse } from "@web-app/types";
import { isHttpError } from "@web-app/utils/errorHandler";
import { timeZoneMap } from "@web-app/utils/helpers";
import http from "@web-app/utils/http";

type UberDeliveryPropsType = {
  entity: string;
  entityId: string;
  externalAssigneeId: string;
  timezone: keyof typeof timeZoneMap;
};

const deliverableActionMap: { [key: string]: string } = {
  deliverable_action_leave_at_door: "Leave at door",
  deliverable_action_meet_at_door: "Meet at door",
};

const undeliverableStatusMap: { [key: string]: string } = {
  left_at_door: "Left at door",
  returned: "Returned",
};

export default function UberDeliveryInfoV2({
  externalAssigneeId,
  entity,
  entityId,
  timezone,
}: UberDeliveryPropsType) {
  const [data, setData] = useState<UberResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deliveryId, setDeliveryId] = useState<string>("");
  const [isCatering, setIsCatering] = useState<boolean>(false);

  useEffect(() => {
    let deliveryId = "";
    let isCatering = false;

    if (externalAssigneeId?.startsWith("uber-catering")) {
      deliveryId = externalAssigneeId?.slice(14) || "";
      isCatering = true;
    } else if (externalAssigneeId?.startsWith("uber")) {
      deliveryId = externalAssigneeId?.slice(5) || "";
    }
    setDeliveryId(deliveryId);
    setIsCatering(isCatering);
  }, [externalAssigneeId]);

  const getDelivery = useCallback(async () => {
    try {
      const resp = await http.get(
        `/integrations/uber/deliveries/${deliveryId}?deliveryType=${isCatering ? "catering" : "normal"}`,
      );

      setData(resp.data);
      setIsLoading(false);
    } catch (err: unknown) {
      // Handle 404 - delivery not found (expected, don't show error)
      if (isHttpError(err, 404)) {
        setData(null);
        setIsLoading(false);
      } else {
        // Other errors - log and show null state
        console.error("Failed to fetch Uber delivery:", err);
        setData(null);
        setIsLoading(false);
      }
    }
  }, [deliveryId, isCatering]);

  const cancelDelivery = useCallback(async () => {
    const resp = await http.post(
      `/integrations/uber/deliveries/${deliveryId}/cancel?deliveryType=${isCatering ? "catering" : "normal"}&entity=${entity}&id=${entityId}`,
    );
    if (resp.status === 200 && resp.data.status === "canceled") {
      toast.success("Delivery canceled successfully");
      setData(null);
      setIsLoading(false);
    }
  }, [deliveryId, isCatering]);

  useEffect(() => {
    getDelivery();
  }, [getDelivery]);

  const getExternalDeliveryFee = () => {
    return (data?.fee ?? 0) / 100;
  };

  const getExternalDeliveryDuration = () => {
    const pickupEta = data?.pickup_eta;
    const dropoffEta = data?.dropoff_eta;
    if (pickupEta && dropoffEta) {
      return dayjs(dropoffEta).diff(dayjs(pickupEta), "minute");
    }
  };

  const getDriverName = () => {
    if (data?.courier) {
      return data.courier.name;
    }
    return "";
  };

  const getDriverPhoneNumber = () => {
    if (data?.courier) {
      return data.courier?.public_phone_info?.formatted_phone_number;
    }
    return "";
  };

  const onRefresh = () => {
    setIsLoading(true);
    getDelivery();
  };

  const onCancel = () => {
    setIsLoading(true);
    cancelDelivery();
  };

  return (
    <div>
      {isLoading ? (
        <LoadingIcon className="w-8 h-8 m-auto mt-10" />
      ) : (
        <div>
          {data === null ? (
            // shouldn't we go to order summary tab instead?
            <div className="text-sm text-gray-500 ml-4">
              No assigned delivery found.
            </div>
          ) : (
            <div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Cost</span>
                  <span className="tw-details-field-value font-semibold">
                    {`$${getExternalDeliveryFee()}`}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Duration</span>
                  <span className="tw-details-field-value flex items-center gap-2">
                    {`${getExternalDeliveryDuration() || "N/A"} minutes`}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">
                    Scheduled End Time
                  </span>
                  <span className="tw-details-field-value">
                    <DateTimeTz
                      start={new Date(data?.dropoff_deadline)}
                      timezone={timezone}
                      presentation="default"
                    />
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">Pickup ETA</span>
                  <span className="tw-details-field-value">
                    <DateTimeTz
                      start={new Date(data?.pickup_eta)}
                      timezone={timezone}
                      presentation="default"
                    />
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title"> Dropoff ETA</span>
                  <span className="tw-details-field-value">
                    <DateTimeTz
                      start={new Date(data?.dropoff_eta)}
                      timezone={timezone}
                      presentation="default"
                    />
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">
                    {" "}
                    Deliverable Action
                  </span>
                  <span className="tw-details-field-value">
                    {deliverableActionMap[data?.deliverable_action || ""] ||
                      "N/A"}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">
                    {" "}
                    Undeliverable Status
                  </span>
                  <span className="tw-details-field-value">
                    {undeliverableStatusMap[data?.undeliverable_action || ""] ||
                      "N/A"}
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title">
                    {" "}
                    Undeliverable Reason
                  </span>
                  <span className="tw-details-field-value">
                    {data?.undeliverable_reason || "N/A"}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title flex items-center">
                    Assignee
                  </span>
                  <span className="flex items-center gap-2">
                    {!getDriverName() && (
                      <span className="tw-details-field-value">
                        No Assignee
                      </span>
                    )}
                    {getDriverName() && (
                      <>
                        <span>
                          <Avatar image={undefined} size={10} />
                        </span>
                        <span className="flex flex-col">
                          <span className="tw-details-field-value !font-semibold break-words">
                            {getDriverName()}
                          </span>
                          <CopyableField
                            icon="fasPhone"
                            value={getDriverPhoneNumber()}
                            appearance="inline"
                            className="text-xs font-normal"
                          />
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2 px-4 py-4 border-b border-gray-200">
                <div className="grid grid-cols-[1fr_2fr] gap-4">
                  <span className="tw-details-field-title flex items-center">
                    Tracking Link
                  </span>
                  <span
                    className="tw-details-field-value"
                    style={{ overflowWrap: "anywhere" }}
                  >
                    <CopyableField
                      value={data?.tracking_url}
                      appearance="link-inline"
                      className="text-xs font-normal"
                    />
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-2 pr-4 pt-2">
                <Button plain size="sm" onClick={onRefresh}>
                  Refresh
                </Button>
                <Button color="red" size="sm" onClick={onCancel}>
                  Cancel Delivery
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
